import axios from "axios";

// Base origin of the Laravel backend (also where uploaded files are served).
export const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";

// Central axios instance for talking to the Laravel backend.
// withCredentials is REQUIRED: the OTP -> registration flow relies on the
// Laravel session cookie, which only travels when credentials are enabled
// (and the backend has CORS supports_credentials=true).
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: {
    Accept: "application/json",
  },
});

// ── System-user (admin/RBAC) bearer token ──────────────────────────────────
// The public/member flow rides the Laravel session cookie (withCredentials).
// The separate back-office "system user" API is token-based (Sanctum): login
// returns a personal access token we store here and send as a Bearer header.
// Attaching it on every request is harmless for the session routes (they ignore
// the header) and required for the /api/system/* routes.
//
// The token lives in sessionStorage, NOT localStorage: it must survive an
// in-tab refresh (so an authenticated operator isn't logged out on reload), but
// it MUST NOT persist across browser sessions. Persisting it would silently
// re-authenticate on the next visit and bounce the user past the login page
// without entering credentials — admin access must require an explicit login.
const TOKEN_KEY = "sfmis_admin_token";
// Member-portal (OTP → Sanctum) token. Kept in a separate key from the admin
// token: the two are distinct token audiences (a member token can't reach admin
// routes and vice-versa), and a browser is only ever in one flow at a time.
const MEMBER_TOKEN_KEY = "sfmis_member_token";

// One-time migration: remove any token left in localStorage by older builds so
// a stale persisted session can't auto-authenticate.
try {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(MEMBER_TOKEN_KEY);
} catch {
  /* storage unavailable — nothing to migrate */
}

export const getAdminToken = () => {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

export const setAdminToken = (token) => {
  try {
    if (token) sessionStorage.setItem(TOKEN_KEY, token);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* storage unavailable (private mode) — token stays in memory only */
  }
};

export const clearAdminToken = () => setAdminToken(null);

// ── Member-portal bearer token ──────────────────────────────────────────────
// Issued by /verify-otp for an existing member (abilities member/coach). Stored
// in sessionStorage for the same reason as the admin token: survive an in-tab
// refresh, but never auto-authenticate across browser sessions.
export const getMemberToken = () => {
  try {
    return sessionStorage.getItem(MEMBER_TOKEN_KEY);
  } catch {
    return null;
  }
};

export const setMemberToken = (token) => {
  try {
    if (token) sessionStorage.setItem(MEMBER_TOKEN_KEY, token);
    else sessionStorage.removeItem(MEMBER_TOKEN_KEY);
  } catch {
    /* storage unavailable (private mode) — token stays in memory only */
  }
};

export const clearMemberToken = () => setMemberToken(null);

// Attach the appropriate Bearer token. System routes always use the admin
// token; member routes prefer the member token; other domain routes use
// whichever token this browser has (admin OR member — never both at once).
api.interceptors.request.use((config) => {
  const url = config.url || "";
  const adminToken = getAdminToken();
  const memberToken = getMemberToken();

  let token;
  if (url.includes("/system/")) token = adminToken;
  else if (url.includes("/member/")) token = memberToken || adminToken;
  else token = adminToken || memberToken;

  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── Server-unavailable sanitization ─────────────────────────────────────────
// When the backend (or the MySQL server underneath it, e.g. XAMPP stopped)
// is unreachable, Laravel's debug-mode error body can carry the raw
// exception message — SQL text, table/column names, DB host/port, file
// paths. Every page pulls its display string straight from
// `error.response.data.message` (one call site per form, no shared error
// component), so rather than touching every page, sanitize it once here —
// the single place all of them read through.
const SERVER_UNAVAILABLE_MESSAGE =
  "We're unable to connect to the server right now. Please try again later.";
const SERVER_UNAVAILABLE_TITLE = "Server Unavailable";
const SERVER_ERROR_STATUSES = [500, 502, 503, 504];

// On a 401 the relevant token is dead/expired: drop it and bounce to the right
// login. System routes → admin login; member-portal routes → member login.
// Other domain 401s are left alone (some are admin-only endpoints the member
// portal deliberately can't reach — those degrade gracefully in the UI rather
// than logging the member out).
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const isServerError = SERVER_ERROR_STATUSES.includes(status);
    // No `error.response` at all means the request never reached the
    // server (backend/XAMPP down, DNS failure, offline) rather than the
    // server replying with an error — axios' own message here ("Network
    // Error") is already generic, but flag it the same way so callers can
    // offer one consistent "Server Unavailable" + Retry treatment either way.
    const isNetworkError = !error?.response && !axios.isCancel(error);

    if (isServerError || isNetworkError) {
      if (isServerError) {
        // Replace whatever Laravel/PHP put in the body — may contain SQL,
        // stack traces, file paths — with a single safe field. Logged to the
        // console (dev-only visibility), never rendered.
        // eslint-disable-next-line no-console
        console.error("[api] server error (details hidden from UI):", error.response?.data);
        error.response.data = { message: SERVER_UNAVAILABLE_MESSAGE };
      } else {
        // eslint-disable-next-line no-console
        console.error("[api] network error (backend unreachable):", error);
      }
      error.isServerUnavailable = true;
      error.friendlyTitle = SERVER_UNAVAILABLE_TITLE;
      error.friendlyMessage = SERVER_UNAVAILABLE_MESSAGE;
      error.message = SERVER_UNAVAILABLE_MESSAGE;
    }

    const url = error?.config?.url || "";
    const isSystemRoute = url.includes("/api/system/");
    const isLogin = url.includes("/api/system/login");
    const isMemberRoute = url.includes("/api/member/");

    if (status === 401 && isSystemRoute && !isLogin) {
      clearAdminToken();
      if (!window.location.pathname.startsWith("/admin/login")) {
        window.location.assign("/admin/login");
      }
    } else if (status === 401 && isMemberRoute) {
      clearMemberToken();
      if (!window.location.pathname.startsWith("/login")) {
        window.location.assign("/login");
      }
    }
    return Promise.reject(error);
  }
);

// Build an absolute URL to a file in the backend's public storage.
// The backend's asset() helper embeds its configured APP_URL host, which can
// differ from the origin the SPA actually talks to (e.g. a LAN IP vs.
// localhost) — that mismatch makes <img> requests fail. We always re-point the
// path at API_BASE so photos load wherever the frontend is served from.
// Accepts either a stored relative path ("photos/x.jpg") or a full URL.
export const storageUrl = (pathOrUrl) => {
  if (!pathOrUrl) return null;
  const s = String(pathOrUrl);
  if (/^https?:\/\//i.test(s)) {
    const idx = s.indexOf("/storage/");
    return idx >= 0 ? `${API_BASE}${s.slice(idx)}` : s;
  }
  return `${API_BASE}/storage/${s.replace(/^\/+/, "")}`;
};

export default api;
